import React, { useState, useEffect } from 'react';
import { 
    TextField, 
    Button, 
    Container, 
    Typography, 
    Paper,
    Box,
    Chip,
    Stack,
    Divider,
    IconButton,
    Snackbar,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { userRequest } from '../../requestMethods';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

const GrammarTopicEditor = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;

    const [topic, setTopic] = useState({
        slug: '',
        title: '',
        summary: '',
        content: '',
        examples: [''],
        tags: [],
        language: '',
        level: ''
    });
    
    const [newTag, setNewTag] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [languages, setLanguages] = useState([]);
    const [learnerLevels, setLearnerLevels] = useState([]);
    
    // Replace static success message with Snackbar state
    const [successSnackbar, setSuccessSnackbar] = useState({
        open: false,
        message: ''
    });

    // Function to handle closing the success snackbar
    const handleCloseSnackbar = () => {
        setSuccessSnackbar({
            ...successSnackbar,
            open: false
        });
    };

    useEffect(() => {
        fetchLanguagesAndLevels();
        if (isEditMode) {
            fetchTopic();
        }
    }, [id]);

    const fetchLanguagesAndLevels = async () => {
        try {
            const [languagesResponse, levelsResponse] = await Promise.all([
                userRequest.get('/language'),
                userRequest.get('/learnerLevel')
            ]);
            setLanguages(languagesResponse.data);
            setLearnerLevels(levelsResponse.data);
        } catch (err) {
            console.error('Error fetching languages and levels:', err);
            setError('Failed to load languages and levels. Please try again.');
        }
    };

    const fetchTopic = async () => {
        setLoading(true);
        try {
            const response = await userRequest.get(`/grammar/${id}`);
            setTopic(response.data);
        } catch (err) {
            console.error('Error fetching grammar topic:', err);
            setError('Failed to load grammar topic. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setTopic(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleExampleChange = (index, value) => {
        const updatedExamples = [...topic.examples];
        updatedExamples[index] = value;
        setTopic(prev => ({
            ...prev,
            examples: updatedExamples
        }));
    };

    const addExample = () => {
        setTopic(prev => ({
            ...prev,
            examples: [...prev.examples, '']
        }));
    };

    const removeExample = (index) => {
        const updatedExamples = [...topic.examples];
        updatedExamples.splice(index, 1);
        setTopic(prev => ({
            ...prev,
            examples: updatedExamples
        }));
    };

    const handleAddTag = () => {
        if (newTag.trim() && !topic.tags.includes(newTag.trim())) {
            setTopic(prev => ({
                ...prev,
                tags: [...prev.tags, newTag.trim()]
            }));
            setNewTag('');
        }
    };

    const handleDeleteTag = (tagToDelete) => {
        setTopic(prev => ({
            ...prev,
            tags: prev.tags.filter(tag => tag !== tagToDelete)
        }));
    };

    const generateSlug = () => {
        const slug = topic.title
            .toLowerCase()
            .replace(/[^\w\s]/gi, '')
            .replace(/\s+/g, '-');
        
        setTopic(prev => ({
            ...prev,
            slug
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        
        try {
            // Create a new object with only the allowed fields
            const allowedUpdates = ['title', 'slug', 'summary', 'content', 'examples', 'tags', 'language', 'level'];
            const topicToSubmit = {};
            
            // Only include the allowed fields
            allowedUpdates.forEach(field => {
                topicToSubmit[field] = topic[field];
            });
            
            if (isEditMode) {
                await userRequest.put(`/grammar/${id}`, topicToSubmit);
                // Show success message as a snackbar
                setSuccessSnackbar({
                    open: true,
                    message: 'Grammar topic updated successfully!'
                });
            } else {
                await userRequest.post('/grammar', topicToSubmit);
                // Show success message as a snackbar
                setSuccessSnackbar({
                    open: true,
                    message: 'Grammar topic created successfully!'
                });
                // Clear form after successful creation
                setTopic({
                    slug: '',
                    title: '',
                    summary: '',
                    content: '',
                    examples: [''],
                    tags: [],
                    language: '',
                    level: ''
                });
            }
        } catch (err) {
            console.error('Error saving grammar topic:', err);
            setError('Failed to save grammar topic. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container maxWidth="lg">
            <Typography variant="h4" component="h1" gutterBottom sx={{ mt: 3 }}>
                {isEditMode ? 'Edit Grammar Topic' : 'Create Grammar Topic'}
            </Typography>

            {/* Success Snackbar */}
            <Snackbar
                open={successSnackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert 
                    onClose={handleCloseSnackbar} 
                    severity="success" 
                    sx={{ width: '100%' }}
                >
                    {successSnackbar.message}
                </Alert>
            </Snackbar>

            {error && (
                <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'error.light' }}>
                    <Typography color="error">{error}</Typography>
                </Paper>
            )}

            <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
                <Box component="form" onSubmit={handleSubmit} sx={{ '& .MuiTextField-root': { mb: 3 } }}>
                    <TextField
                        label="Title"
                        name="title"
                        value={topic.title}
                        onChange={handleInputChange}
                        fullWidth
                        required
                        onBlur={generateSlug}
                    />

                    <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                        <TextField
                            label="Slug"
                            name="slug"
                            value={topic.slug}
                            onChange={handleInputChange}
                            fullWidth
                            required
                            helperText="URL-friendly identifier (auto-generated from title)"
                        />
                        <Button 
                            variant="outlined" 
                            onClick={generateSlug}
                            sx={{ height: 56 }}
                        >
                            Generate
                        </Button>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                        <FormControl fullWidth required>
                            <InputLabel>Language</InputLabel>
                            <Select
                                name="language"
                                value={topic.language}
                                onChange={handleInputChange}
                                label="Language"
                            >
                                {languages.map((language) => (
                                    <MenuItem key={language._id} value={language._id}>
                                        {language.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth required>
                            <InputLabel>Level</InputLabel>
                            <Select
                                name="level"
                                value={topic.level}
                                onChange={handleInputChange}
                                label="Level"
                            >
                                {learnerLevels.map((level) => (
                                    <MenuItem key={level._id} value={level._id}>
                                        {level.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    <TextField
                        label="Summary"
                        name="summary"
                        value={topic.summary}
                        onChange={handleInputChange}
                        fullWidth
                        required
                        multiline
                        rows={2}
                    />

                    <TextField
                        label="Content"
                        name="content"
                        value={topic.content}
                        onChange={handleInputChange}
                        fullWidth
                        required
                        multiline
                        rows={10}
                    />

                    <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>Examples</Typography>
                    
                    {topic.examples.map((example, index) => (
                        <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            <TextField
                                label={`Example ${index + 1}`}
                                value={example}
                                onChange={(e) => handleExampleChange(index, e.target.value)}
                                fullWidth
                            />
                            <IconButton 
                                color="error" 
                                onClick={() => removeExample(index)}
                                disabled={topic.examples.length <= 1}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Box>
                    ))}

                    <Button 
                        startIcon={<AddIcon />} 
                        onClick={addExample} 
                        variant="outlined"
                        sx={{ mb: 3 }}
                    >
                        Add Example
                    </Button>

                    <Divider sx={{ mb: 3 }} />

                    <Typography variant="h6" sx={{ mb: 2 }}>Tags</Typography>
                    
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <TextField
                            label="Add Tag"
                            value={newTag}
                            onChange={(e) => setNewTag(e.target.value)}
                            fullWidth
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddTag();
                                }
                            }}
                        />
                        <Button 
                            variant="outlined" 
                            onClick={handleAddTag}
                            sx={{ height: 56 }}
                        >
                            Add
                        </Button>
                    </Box>

                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 3 }}>
                        {topic.tags.map((tag, index) => (
                            <Chip
                                key={index}
                                label={tag}
                                onDelete={() => handleDeleteTag(tag)}
                            />
                        ))}
                    </Stack>

                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button 
                            variant="outlined" 
                            onClick={() => navigate('/grammar')}
                        >
                            Cancel
                        </Button>
                        <Button 
                            type="submit" 
                            variant="contained" 
                            disabled={loading}
                        >
                            {isEditMode ? 'Update' : 'Create'}
                        </Button>
                    </Box>
                </Box>
            </Paper>
            
            <div style={{ height: '60px' }}></div>
        </Container>
    );
};

export default GrammarTopicEditor; 